import React from 'react';
import { Calendar, MapPin, Briefcase } from 'lucide-react';

const Experience = () => {
  // EASY UPDATE: Add or modify experience entries here
  // Technology icons: Just add the name and the Font Awesome icon will automatically show!
  const experiences = [
    {
      company: 'GE Aerospace Poland Sp. z o.o.',
      position: 'IT Helpdesk Intern',
      location: 'Poland',
      startDate: 'May 2025',
      endDate: 'August 2025',
      description: 'Helped and maintained multiple appliances for users across the GE Aerospace Site.',
      achievements: [
        'Re-enrolled 200+ company laptops to new required standard',
        'Improved efficiency in accessing key intranet components for new hires',
        'Gained hands-on experience with Windows, Azure, and networking',
      ],
      // EASY UPDATE: Just add technology names, Font Awesome icons will show automatically
      technologies: ['Windows', 'Azure', 'Networking', 'HTML/CSS'],
    },
  ];

  // Font Awesome icon mapping - automatically shows icons based on tech name
  const getTechIcon = (tech) => {
    const iconMap = {
      // Microsoft & Cloud
      'Windows': 'fab fa-windows',
      'Microsoft': 'fab fa-microsoft',
      'Azure': 'fab fa-microsoft',
      'AWS': 'fab fa-aws',
      'Google Cloud': 'fab fa-google',
      
      // Frontend
      'HTML': 'fab fa-html5',
      'CSS': 'fab fa-css3-alt',
      'JavaScript': 'fab fa-js',
      'TypeScript': 'fab fa-js',
      'React': 'fab fa-react',
      'Vue.js': 'fab fa-vuejs',
      'Vue': 'fab fa-vuejs',
      'Angular': 'fab fa-angular',
      'Next.js': 'fab fa-react',
      'HTML/CSS': 'fab fa-html5',
      'Tailwind': 'fab fa-css3-alt',
      
      // Backend
      'Node.js': 'fab fa-node-js',
      'Node': 'fab fa-node-js',
      'Python': 'fab fa-python',
      'Java': 'fab fa-java',
      'PHP': 'fab fa-php',
      'C#': 'fas fa-code',
      'Go': 'fas fa-code',
      'Ruby': 'fas fa-gem',
      'FastAPI': 'fas fa-bolt',
      'Express': 'fab fa-node',
      'Django': 'fas fa-code',
      'Laravel': 'fab fa-laravel',
      
      // Databases
      'MySQL': 'fas fa-database',
      'PostgreSQL': 'fas fa-database',
      'MongoDB': 'fas fa-database',
      'Redis': 'fas fa-database',
      'SQL': 'fas fa-database',
      'Database': 'fas fa-database',
      
      // DevOps & Tools
      'Docker': 'fab fa-docker',
      'Kubernetes': 'fas fa-dharmachakra',
      'Git': 'fab fa-git-alt',
      'GitHub': 'fab fa-github',
      'GitLab': 'fab fa-gitlab',
      'CI/CD': 'fas fa-code-branch',
      'Linux': 'fab fa-linux',
      'Ubuntu': 'fab fa-ubuntu',
      'Bash': 'fas fa-terminal',
      'Jenkins': 'fab fa-jenkins',
      
      // Networking & Security
      'Networking': 'fas fa-network-wired',
      'Network': 'fas fa-network-wired',
      'Security': 'fas fa-shield-halved',
      'Firewall': 'fas fa-shield-halved',
      
      // Other
      'API': 'fas fa-plug',
      'REST': 'fas fa-plug',
      'GraphQL': 'fas fa-project-diagram',
      'Testing': 'fas fa-vial',
      'Agile': 'fas fa-sync',
      'WordPress': 'fab fa-wordpress',
      'Shopify': 'fab fa-shopify',
      'npm': 'fab fa-npm',
      'Yarn': 'fab fa-yarn',
    };
    
    return iconMap[tech] || 'fas fa-cog'; // Default gear icon
  };

  return (
    <section id="experience" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Experience</h2>
          <div className="w-20 h-1 bg-[#BCA4E3] mx-auto"></div>
        </div>

        {/* Experience Timeline */}
        <div className="max-w-5xl mx-auto space-y-8">
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="group relative overflow-hidden bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-6 hover:border-[#BCA4E3]/30 hover:shadow-lg hover:shadow-[#BCA4E3]/5 transition-all duration-300"
            >
              {/* Left Border Accent */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#BCA4E3] to-transparent rounded-l-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="flex flex-col gap-4">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-white mb-1 group-hover:text-[#BCA4E3] transition-colors">
                      {exp.position}
                    </h3>
                    <p className="text-base text-[#BCA4E3] font-medium mb-2">
                      {exp.company}
                    </p>
                  </div>
                  
                  {/* Date & Location */}
                  <div className="flex flex-col sm:items-end gap-1 text-sm text-slate-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{exp.startDate} - {exp.endDate}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{exp.location}</span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-300 text-sm leading-relaxed">
                  {exp.description}
                </p>

                {/* Achievements */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-[#BCA4E3]" />
                    Key Achievements
                  </h4>
                  <ul className="space-y-1.5 ml-6">
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex} className="text-sm text-slate-300 flex items-start">
                        <span className="text-[#BCA4E3] mr-2 mt-0.5">•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technology Tags with Font Awesome Icons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {exp.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/50 border border-slate-700/50 text-slate-300 text-xs font-medium rounded-full hover:border-[#BCA4E3]/30 hover:bg-slate-800 transition-all duration-200"
                    >
                      <i className={getTechIcon(tech)} style={{ fontSize: '0.875rem' }}></i>
                      <span>{tech}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
